import ColorizeIcon from "@mui/icons-material/Colorize";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PiColorPickerField from "../../../components/PiColorPickerField";
import PiInputfield from "../../../components/PiInputField";
import {
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../../app/slices/GlobalSlice";
import Interaction from "../../../models/interactions/Interaction";
import {
  IProximityTrigger,
} from "../../../models/interactions/ProximityTrigger";
import { ProximityTriggerMethod } from "../../../models/interactions/ProximityTrigger.shared";
import NodeActorDropdown from "../components/NodeActorDropdown";
import NodeProximityPositionDropdown from "../components/NodeProximityPositionDropdown";
import NodeStringDropdown from "../components/NodeStringDropdown";
import NodeToggle from "../components/NodeToggle";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";

const methodOptions = [
  {
    label: "On Enter",
    value: ProximityTriggerMethod.OnEnter,
  },
  {
    label: "On Exit",
    value: ProximityTriggerMethod.OnExit,
  },
];

const parseNumber = (value: string, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export default function ProximityTriggerNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const interaction = props.interaction as IProximityTrigger;
  const { updateInteraction } = useUpdateSequenceInteraction();
  const dispatch = useDispatch();
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);
  const [expanded, setExpanded] = useState(false);

  const isPickerActiveForNode =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.nodeId === props.nodeId &&
    sequencePositionPicker.positionType === "proximity";

  const positionDropdown = useMemo(
    () => (
      <NodeProximityPositionDropdown
        label="Position"
        selectedPositionId={interaction.posId}
        accessory={
          <button
            type="button"
            aria-label="Pick on map"
            title="Pick on map"
            className={
              "group flex h-[32px] w-[32px] items-center justify-center rounded-full border-[2px] transition-all duration-150 " +
              (isPickerActiveForNode
                ? "border-creator-primary bg-creator-primary text-white shadow-[0_0_0_4px_rgba(58,196,255,0.18)]"
                : "border-creator-primary bg-creator-primary-background text-creator-primary hover:bg-creator-primary-washed")
            }
            onClick={() =>
              dispatch(
                openSequencePositionPicker({
                  nodeId: props.nodeId,
                  positionType: "proximity",
                  title: "Pick proximity position",
                  presentation: "splitView",
                }),
              )
            }
          >
            <ColorizeIcon fontSize="inherit" className="text-[18px]" />
          </button>
        }
      />
    ),
    [
      dispatch,
      isPickerActiveForNode,
      props.nodeId,
    ],
  );

  const actorDropdown = useMemo(
    () => (
      <NodeActorDropdown
        label="Actor"
        selectedActorId={interaction.npcId}
        onClick={(npcId) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            npcId,
          } as IProximityTrigger)
        }
      />
    ),
    [interaction, props.nodeId, updateInteraction],
  );

  const distanceInput = useMemo(
    () => (
      <PiInputfield
        label="Distance"
        placeholder="Distance"
        value={interaction.distance.toString()}
        onChange={(distance) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            distance: parseNumber(distance, interaction.distance),
          } as IProximityTrigger)
        }
      />
    ),
    [interaction, props.nodeId, updateInteraction],
  );

  const methodDropdown = useMemo(
    () => (
      <div className="flex flex-col gap-[8px]">
        <div className="font-medium text-creator-text text-[14px]">Method</div>
        <NodeStringDropdown
          selectedItem={
            methodOptions.find((option) => option.value === interaction.method)
              ?.label ?? ""
          }
          items={methodOptions.map((option) => option.label)}
          label={undefined}
          onClick={(selectedLabel) =>
            updateInteraction(props.nodeId, {
              ...interaction,
              method:
                methodOptions.find((option) => option.label === selectedLabel)
                  ?.value ?? interaction.method,
            } as IProximityTrigger)
          }
        />
      </div>
    ),
    [interaction, props.nodeId, updateInteraction],
  );

  const visualizeToggle = useMemo(
    () => (
      <NodeToggle
        label="Visualize"
        value={interaction.visualize}
        onChange={(visualize) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            visualize,
          } as IProximityTrigger)
        }
      />
    ),
    [interaction, props.nodeId, updateInteraction],
  );

  const lineWeightInput = useMemo(
    () => (
      <PiInputfield
        label="Line Weight"
        placeholder="Line Weight"
        value={interaction.lineWeight.toString()}
        onChange={(lineWeight) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            lineWeight: parseNumber(lineWeight, interaction.lineWeight),
          } as IProximityTrigger)
        }
      />
    ),
    [interaction, props.nodeId, updateInteraction],
  );

  const outlineColor = useMemo(
    () => (
      <PiColorPickerField
        label="Outline Color"
        color={interaction.colorOutline}
        onChange={(colorOutline) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            colorOutline,
          } as IProximityTrigger)
        }
      />
    ),
    [interaction, props.nodeId, updateInteraction],
  );

  const fillColor = useMemo(
    () => (
      <PiColorPickerField
        label="Fill Color"
        color={interaction.colorFill}
        onChange={(colorFill) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            colorFill,
          } as IProximityTrigger)
        }
      />
    ),
    [interaction, props.nodeId, updateInteraction],
  );

  return (
    <div className="flex flex-col gap-[12px] w-full h-full">
      {positionDropdown}
      {actorDropdown}
      {distanceInput}
      {methodDropdown}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className="flex flex-col gap-[12px] overflow-hidden h-full"
            initial={{ opacity: 0, height: 0 }}
            animate={{
              opacity: 1,
              height: "auto",
              transition: { duration: 0.1 },
            }}
            exit={{ opacity: 0, height: 0, transition: { duration: 0.1 } }}
          >
            {visualizeToggle}
            {lineWeightInput}
            {outlineColor}
            {fillColor}
          </motion.div>
        )}
      </AnimatePresence>
      <div
        data-testid="proximity-trigger-expand-button"
        className="flex flex-row gap-2 w-full h-full text-[24px] items-center justify-center text-creator-primary cursor-pointer hover:text-creator-primary-hover"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <KeyboardDoubleArrowUpIcon fontSize="inherit" color="inherit" />
        ) : (
          <KeyboardDoubleArrowDownIcon fontSize="inherit" color="inherit" />
        )}
      </div>
    </div>
  );
}
